import React from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import LessonLayout from "./LessonLayout";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtShort = (n) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1_000)}k`);

// Two retirees, same average return, different sequence.
// Retiree A: bad returns early, good returns late.
// Retiree B: good returns early, bad returns late.
// Both start with $1M and withdraw $40k/yr (adjusted 2%/yr for inflation).
const seqA = [-0.15, -0.10, -0.05, 0.05, 0.08, 0.12, 0.15, 0.18, 0.14, 0.10];
const seqB = [0.14, 0.15, 0.12, 0.10, 0.08, 0.05, -0.05, -0.10, -0.15, 0.10];

const buildPath = () => {
  let a = 1_000_000;
  let b = 1_000_000;
  const seq = seqA.length;
  const years = 30;
  const path = [{ year: 0, retiree_a: a, retiree_b: b }];
  for (let y = 1; y <= years; y++) {
    // Reuse the 10-year sequence 3 times (30 years total)
    const idx = (y - 1) % seq;
    a = a * (1 + seqA[idx]) - 40_000;
    b = b * (1 + seqB[idx]) - 40_000;
    path.push({ year: y, retiree_a: Math.max(a, 0), retiree_b: Math.max(b, 0) });
  }
  return path;
};

export default function LearnSequenceRisk() {
  const data = buildPath();
  const finalA = data[data.length - 1].retiree_a;
  const finalB = data[data.length - 1].retiree_b;

  return (
    <LessonLayout
      meta={{
        number: 5,
        slug: "sequence-risk",
        time: "6 min read",
        title: "Sequence of returns — the silent killer.",
        hook: "Two people retire on the exact same day, with the exact same $1M, the exact same average return over 30 years. One dies with $3M in the bank. The other dies broke.",
        quickAnswer:
          "Sequence-of-returns risk is the danger that poor market returns early in retirement — while you are withdrawing — permanently damage your portfolio, even when long-run average returns are identical. Defenses: a lower starting withdrawal rate, 1–2 years of cash buffer, flexible spending guardrails, and stress-testing your plan against real sequences like 1929 and 2008.",
        seoDescription: "Interactive lesson on sequence-of-returns risk. Two retirees with the same average returns can have wildly different outcomes depending on the order of good and bad years.",
        bookCategory: "retirement",
      }}
      next={null}
    >
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} unit=" yr" />
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} />
            <Tooltip formatter={(v) => fmt(v)} labelFormatter={(y) => `Year ${y}`} contentStyle={{ backgroundColor: "#fff", border: "1px solid #E5E2DC", borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="retiree_a" stroke="#C26747" strokeWidth={3} dot={false} name="Retiree A — bad markets early" />
            <Line type="monotone" dataKey="retiree_b" stroke="#2B5339" strokeWidth={3} dot={false} name="Retiree B — good markets early" />
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="bg-[#FDF2ED] border border-[#F5C7B4] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#C26747] font-semibold mb-1">
              Retiree A after 30 years
            </div>
            <div className="font-mono-num text-2xl text-[#C26747]">{finalA > 0 ? fmt(finalA) : "Broke"}</div>
          </div>
          <div className="bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#2B5339] font-semibold mb-1">
              Retiree B after 30 years
            </div>
            <div className="font-mono-num text-2xl text-[#2B5339]">{fmt(finalB)}</div>
          </div>
        </div>

        <div className="mt-4 text-xs text-[#5C6B62] leading-relaxed">
          Both retirees withdrew $40k/yr for 30 years. Both experienced <strong>the same set of annual returns</strong> —
          just in different order. Same market, opposite outcomes.
        </div>
      </div>

      <div className="prose prose-sm max-w-none text-[#1C2B23] space-y-4 leading-relaxed">
        <h2 className="text-2xl font-medium">Why the order matters</h2>
        <p>
          When you&apos;re still working and saving, market crashes are a <strong>gift</strong> — you buy more shares
          at cheaper prices. When you&apos;re retired and withdrawing, market crashes are a <strong>catastrophe</strong> — you&apos;re
          forced to sell shares at cheap prices to fund your life.
        </p>
        <p>
          A retiree who hits their first 5 years of retirement during a bear market can permanently damage their
          portfolio. The math is: you sold at the worst possible time, and now you have fewer shares to grow when
          the market recovers.
        </p>

        <h2 className="text-2xl font-medium mt-8">This is why the &ldquo;average return&rdquo; is a lie</h2>
        <p>
          Financial calculators love to tell you &ldquo;the market averages 10%.&rdquo; But you don&apos;t experience the average.
          You experience the actual year-by-year sequence, and if you get unlucky with WHEN the bad years happen,
          you can go broke on the same average returns that made someone else rich.
        </p>

        <h2 className="text-2xl font-medium mt-8">How real retirees defend against this</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Bond tent</strong>: hold more bonds in the 5 years before and 5 years after retirement. Rebalance back to stocks once you&apos;re past the danger zone.</li>
          <li><strong>Cash buffer</strong>: keep 2 years of living expenses in cash so you never have to sell stocks during a crash.</li>
          <li><strong>Flexible withdrawals</strong>: use a &ldquo;guardrails&rdquo; strategy that cuts spending in down years and raises it in up years. This alone can turn a 60% survival plan into a 95% one.</li>
          <li><strong>Delay retirement 6–12 months</strong> if the market drops right before you were going to retire. Uncomfortable, but far better than getting locked into permanent portfolio damage.</li>
        </ul>

        <h2 className="text-2xl font-medium mt-8">The takeaway</h2>
        <p className="text-[#2B5339] font-medium">
          You can&apos;t control when you&apos;re born and when markets crash. You CAN control how much cushion you
          build and how flexible your spending is when it happens. The <a className="text-[#2B5339] underline" href="/">
          main Centennial calculator</a> lets you stress-test your own plan against 1929, 1973, 2000, and 2008 —
          the real historical crashes.
        </p>
      </div>
    </LessonLayout>
  );
}
